import type { GetStaticPropsContext } from 'next';

import { PageCategory } from '~/features/analytics/constants';
import { HomepageCTASection } from '~/features/experiments/ab_plp_redesign_2025/components/homepage-cta-section';
import { HomepageMainHeroSection } from '~/features/experiments/ab_plp_redesign_2025/components/homepage-hero-section';
import {
  PartnershipsSection,
  StaticPartnershipsSectionProps,
  transformStaticPartnershipsSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/partnerships-section';
import { UdacityImpactSection } from '~/features/experiments/ab_plp_redesign_2025/components/udacity-impact-section';
import {
  StaticUdacityOutcomesSectionProps,
  transformStaticUdacityOutcomesSectionProps,
  UdacityOutcomesSection,
} from '~/features/experiments/ab_plp_redesign_2025/components/udacity-outcomes-section';
import {
  StaticUdacityResourcesSectionProps,
  transformStaticUdacityResourcesSectionProps,
  UdacityResourcesSection,
} from '~/features/experiments/ab_plp_redesign_2025/components/udacity-resources-section';
import { UdacitySchoolsSection } from '~/features/experiments/ab_plp_redesign_2025/components/udacity-schools-section';
import { UdacityTopProgramsSection } from '~/features/experiments/ab_plp_redesign_2025/components/udacity-top-programs-section';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useSessionReplay } from '~/features/session-replay';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

export default function HomePage({
  page,
  staticUdacityOutcomesSectionProps,
  staticPartnershipsSectionProps,
  staticUdacityResourcesSectionProps,
}: {
  page: Page;
  staticUdacityOutcomesSectionProps: StaticUdacityOutcomesSectionProps;
  staticPartnershipsSectionProps: StaticPartnershipsSectionProps;
  staticUdacityResourcesSectionProps: StaticUdacityResourcesSectionProps;
}) {
  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useSessionReplay();

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.Home,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Home} />
      <PageLayout withoutVerticalPadding>
        <HomepageMainHeroSection />
        <UdacityImpactSection />
        <UdacityTopProgramsSection
          analyticsAction='homepage_ndcard_clicked'
          analyticsEventName='Homepage CTA Clicked'
        />
        <PartnershipsSection {...staticPartnershipsSectionProps} />
        <UdacityOutcomesSection {...staticUdacityOutcomesSectionProps} />
        <HomepageCTASection />
        <UdacitySchoolsSection>
          <UdacityResourcesSection {...staticUdacityResourcesSectionProps} />
        </UdacitySchoolsSection>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page] = await Promise.all([getPage('/')]);

  const staticUdacityOutcomesSectionProps = await transformStaticUdacityOutcomesSectionProps();
  const staticPartnershipsSectionProps = await transformStaticPartnershipsSectionProps();
  const staticUdacityResourcesSectionProps = await transformStaticUdacityResourcesSectionProps();

  return {
    props: {
      page,
      staticUdacityOutcomesSectionProps,
      staticPartnershipsSectionProps,
      staticUdacityResourcesSectionProps,
    },
  };
}
